import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, Lock, AlertTriangle, Eye } from "lucide-react";
import { useGarage } from "@/context/GarageContext";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { t } from "@/i18n";

function Bar({ tone, icon: Icon, children, testId }) {
  const tones = {
    red: "bg-red-600 text-white",
    amber: "bg-amber-100 text-amber-950 border-b border-amber-200",
    navy: "bg-teal-700 text-white",
  };
  return (
    <div data-testid={testId} className={`flex items-start gap-3 px-4 py-3 text-sm sm:px-6 lg:px-10 ${tones[tone]}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function GarageBanners() {
  const { garage, access, isManager } = useGarage();
  const [sessions, setSessions] = useState([]);
  const tz = garage.timezone;

  useEffect(() => {
    if (!isManager) return;
    api.get("/garage/support-sessions").then(({ data }) => setSessions(data.filter((s) => s.active))).catch(() => setSessions([]));
  }, [isManager]);

  const s = access.support_session;
  return (
    <>
      {s && (
        <Bar tone="navy" icon={Eye} testId="support-mode-banner">
          <strong>{t("support.modeTitle")}</strong> — {t("support.modeText", { reason: s.reason, time: fmtDateTime(s.expires_at, tz) })}
        </Bar>
      )}
      {access.blocked && access.awaiting_card && (
        <Bar tone="red" icon={Lock} testId="awaiting-card-banner">
          {t("p7.billing.awaitingCard")}{" "}
          {isManager && <Link to="/pro/settings?tab=plan" className="font-semibold underline" data-testid="awaiting-card-link">{t("p7.billing.subscribe")}</Link>}
        </Bar>
      )}
      {access.blocked && !access.awaiting_card && (
        <Bar tone="red" icon={Lock} testId="suspended-banner">
          <strong>{t("suspension.bannerTitle", { status: t(`status.${access.subscription_status}`) })}</strong> {t("suspension.bannerText")}{" "}
          {isManager && <Link to="/pro/settings?tab=plan" className="font-semibold underline" data-testid="suspended-subscribe-link">{t("p7.billing.subscribe")}</Link>}{" "}
          {isManager && <Link to="/pro/settings?tab=data" className="underline" data-testid="suspension-policy-link">{t("suspension.viewPolicy")}</Link>}
        </Bar>
      )}
      {access.subscription_status === "past_due" && (
        <Bar tone="amber" icon={AlertTriangle} testId="past-due-banner">{t("suspension.pastDue")}</Bar>
      )}
      {sessions.map((x) => (
        <Bar key={x.id} tone="amber" icon={ShieldAlert} testId="manager-support-session-banner">
          {t("support.managerNotice", { reason: x.reason, time: fmtDateTime(x.expires_at, tz) })}
        </Bar>
      ))}
    </>
  );
}
